import React, { useLayoutEffect, useReducer, useRef} from "react";
import { Radio, Select, Switch} from 'antd';
import * as utils from "../lib/Utils";
import {useDebouncedEffect} from "../uiUtils/hooks";
import { isEqual, merge} from 'lodash';
import {Label} from "../uiUtils/Helpers";
import {DebounceNumber} from "../uiUtils/DebouncedNumber";
import {columnSizes, getIndexOfClosest, getStandardColumns} from "../lib/sizeUtils";

export const { Option} = Select;

const debugDefault = true;
function log(debug,...rest) {
    if (debug) {
        console.log(...rest);
    }
}


const printInit = initialDimensions => {
    if (!initialDimensions) return {};
    const {widthInches, heightInches} = initialDimensions;
    const stdCols = getStandardColumns(widthInches);
    return {
        widthInches, heightInches,
        ...stdCols,
        name: getName(stdCols)
    };
}

const getName = ({standard, columns}) =>
    standard==='custom'?'Custom Width':`${columns} column (${standard})`;

// todo ? was this really better...
const printReducer = (oldState, action) => {

    let newState = Object.assign({}, oldState);
    // let changed = false;  // todo ?bother checking almost certianly has

    const setWidth = () => {
        const {standard, columns} = newState;
        if (standard in columnSizes) {
            newState.widthInches = columnSizes[standard][columns - 1];
        }
    };

    // cases are not scoped...
    let widthInches, heightInches;

    switch (action.type) {
        case 'widthInches':
            const newWidthInches = utils.round(action.value, 3);
            if (oldState.widthInches !== newWidthInches ) {
                newState.widthInches = newWidthInches;
                // todo ? do we always want to change to PASS/SAU if it matches (or leave custom)
                merge(newState, getStandardColumns(newState.widthInches));
            }
            break;

        case 'heightInches':
            newState.heightInches = utils.round(action.value, 3);
            break;

        case 'dimensions': // controlled values (inches)
            ({width: widthInches, height: heightInches} = action.value);
            if (oldState.widthInches !== widthInches || oldState.heightInches !== heightInches) {
                //const currentRatio =  heightInches /widthInches;
                newState = {
                    widthInches, heightInches,
                    //keepAspectRatio: oldState.keepAspectRatio && oldState.ratio === currentRatio,
                    //ratio: oldState.ratio,
                    ...getStandardColumns(widthInches)
                };
            }
            break;

        case 'keepAspectRatio':
            newState.keepAspectRatio = action.value;
            break;

        case 'defaultRatio':
            ({widthInches, heightInches} = action.value);
            newState.ratio = heightInches /widthInches;
            break;

        case 'standard':
            newState.standard = action.value;
            if (oldState.standard === 'custom'){
                newState.columns = getIndexOfClosest(oldState.width, columnSizes[newState.standard]);
            }
            setWidth();
            break;

        case 'columns':
            newState.columns = action.value;
            setWidth();
            break;

        case 'reset': // i.e. original dimensions from tew template
            newState =  printInit(action.value);
            break;

        default:
            throw new Error("Unsupported reducer action: "+action.type);
    }
/*
    if (newState.keepAspectRatio &&
            (oldState.widthInches !== newState.widthInches || !oldState.keepAspectRatio ||
                oldState.ratio !== newState.ratio))
    {
        const {widthInches, ratio} = newState;
        newState.heightInches = utils.round(widthInches * ratio,3);
    }

 */

    newState.name = getName(newState);
    //console.log('Print action: ',action, newState);
    return isEqual(newState, oldState) ? oldState : newState;
}


//todo ? Don't keep own width & height (like  WebOutput - its passed in)
// - ? intercepting width change to set height as well
export function PrintOutput(
    {
         onChange= ()=>{},
         onNameChange= ()=>{},
         debug= debugDefault,
         dimensions,
         keepAspectRatio, setKeepAspectRatio,
         template
 })
{
    const [print, printDispatch] = useReducer(printReducer, template, printInit);
    const dispatch = (type,value) => printDispatch({type,value});
    const widthRef = useRef(null);

    /*
    useEffect(()=> {
        if (template) {
            dispatch('defaultRatio', template)
        }
    }, [template]);
*/
    useLayoutEffect(()=> {
        dispatch('dimensions', dimensions);
    },[dimensions.width, dimensions.height]);

    const changeStandard = (newStandard)=> {
        dispatch('standard', newStandard);
        if (newStandard==='custom') {
            widthRef.current.focus();
        }
    };

    //todo - do we still need to debounce this
    useDebouncedEffect(()=>{
        //log(debug,'print pushing dimensions back', print);
        onChange({width: print.widthInches, height: print.heightInches});
    },100,[print.widthInches, print.heightInches]);

    useDebouncedEffect(()=>{
        //log(debug,'print pushing info back');
        onNameChange(print);  //todo ?just pick values
    },100,[print.name]);

    return (
        <>
            <Label>Columns</Label>
            <Radio.Group onChange={e=>changeStandard(e.target.value)} value={print.standard}
                         buttonStyle="solid" size={'small'}>
                <Radio.Button value="PASS">PASS</Radio.Button>
                <Radio.Button value="SAU">SAU</Radio.Button>
                <Radio.Button value="custom">custom</Radio.Button>
            </Radio.Group>
            {print.standard !== 'custom' && <>
                <Label> #</Label>
                <Select onChange={cols=>dispatch('columns',cols)} value={print.columns} size={'small'}>
                    {[1,2,3,4,5,6].map((c)=>
                        <Option key={c} value={c}>{c}</Option>
                    )}
                </Select>
            </>}
            <p/>
            <Label>Width</Label>
            <DebounceNumber  min={0} max={13}
                             size={'small'}
                             onChange={(w=>dispatch('widthInches',w))} value={print.widthInches}
                             step={0.25} ref={widthRef}
                             disabled={print.standard!=='custom'}
            />
            <span> (unit=inches)</span>
            <p/>
            <Label>Depth</Label>
            <DebounceNumber  min={0} max={21}
                             size={'small'}
                             onChange={(height)=>dispatch('heightInches',height)} value={print.heightInches}
                             step={0.25} disabled={print.keepAspectRatio}/>
            <Switch size="small"
                    checked={keepAspectRatio}
                    onChange={setKeepAspectRatio}
                    style={{marginLeft:5}}/>
            <span> Use template proportions</span>
        </>
    );
}